<template>
  <div>
    <validation-observer v-slot="{ invalid, valid, errors, ...rest }" ref="form">
      <form @submit.prevent="submit">
        <header-with-help-button :title="pageTitle" :helpDocId="helpButtonUrl">
          <div class="d-flex">
            <div v-show="!editMode">
              <c-button
                theme="primary"
                size="sm"
                icon="fa-regular fa-pen-to-square"
                @click="showEditMode"
              >Edit</c-button>
            </div>

            <div v-show="editMode">
              <c-button
                type="submit"
                theme="success"
                size="sm"
                icon="fa-regular fa-save"
                @click="submit"
                :showIndicator="showIndicator"
              >Save</c-button>

              <c-button
                theme="default"
                size="sm"
                icon="fa-regular fa-xmark"
                @click="hideEditMode"
              >Cancel</c-button>
            </div>
          </div>
        </header-with-help-button>

        <p class="text-info" v-if="infoTitle">
          <i class="fa-duotone fa-square-info fa-lg mr-half"></i>
          <span v-html="infoTitle"></span>
        </p>
        <h4 class="text-info mb-1" v-if="infoDescription">
          <i>{{ infoDescription }}</i>
        </h4>

        <div v-if="isFetching">
          <c-spinner />
        </div>

        <div class="row" v-show="!editMode">
          <div class="col-xs-12" v-for="(field, index) in fields" :key="`preview-${index}`">
            <h4>
              {{ field.label }}
              <svg v-show="field.type === 'checkbox' && item[`${field.name}`]" class="svg-inline--fa fa-circle-check text-success" aria-hidden="true" focusable="false" data-prefix="fad" data-icon="circle-check" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" data-fa-i2svg=""><g class="fa-duotone-group"><path class="fa-secondary" fill="currentColor" d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z"></path><path class="fa-primary" fill="currentColor" d="M369 175c9.4 9.4 9.4 24.6 0 33.9L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0z"></path></g></svg>

              <svg v-show="field.type === 'checkbox' && !item[`${field.name}`]" class="svg-inline--fa fa-circle-xmark text-danger" aria-hidden="true" focusable="false" data-prefix="fad" data-icon="circle-xmark" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" data-fa-i2svg=""><g class="fa-duotone-group"><path class="fa-secondary" fill="currentColor" d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM175 175c9.4-9.4 24.6-9.4 33.9 0l47 47 47-47c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-47 47 47 47c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-47-47-47 47c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l47-47-47-47c-9.4-9.4-9.4-24.6 0-33.9z"></path><path class="fa-primary" fill="currentColor" d="M209 175c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l47 47-47 47c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l47-47 47 47c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-47-47 47-47c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-47 47-47-47z"></path></g></svg>
            </h4>
            <p v-if="field.type !== 'checkbox'" class="text-wrap" v-html="item[`${field.name}`]" v-show="item[`${field.name}`]"></p>
            <p v-if="!item[`${field.name}`] && field.type !== 'checkbox'">No Data Yet</p>
          </div>
        </div>

        <div class="row" v-show="editMode">
          <div class="col-xs-12">
            <c-select
              v-if="templates && templates.length"
              :empty-option="true"
              empty-option-label="- Select -"
              label="Select Template"
              v-model="templateId"
              :options="templates">
            </c-select>

            <fragment v-for="(item, index) in fields" :key="`field-${index}`">
              <validation-provider
                :vid="item.name"
                :rules="item.isRequired && 'required'"
                v-slot="{ errors }"
              >
                <c-input
                  v-if="item.type === 'text'"
                  :type="item.inputType ? item.inputType : 'text'"
                  :label="item.label"
                  :error="errors[0]"
                  :required-mark="item.isRequired"
                  v-model="currentItem[item.name]"
                  :help="item.help"
                />

                <c-textarea
                  v-if="item.type === 'textarea'"
                  :label="item.label"
                  :error="errors[0]"
                  :required-mark="item.isRequired"
                  :rows="item.rows"
                  v-model="currentItem[item.name]"
                />

                <c-editor
                  :id="`editor-${index}`"
                  v-if="item.type === 'editor'"
                  :label="item.label"
                  :error="errors[0]"
                  :required-mark="item.isRequired"
                  v-model="currentItem[item.name]"
                />

                <c-checkbox
                  v-if="item.type === 'checkbox'"
                  :label="item.label"
                  :error="errors[0]"
                  v-model="currentItem[item.name]"
                />
              </validation-provider>
            </fragment>

            <c-checkbox
              v-if="showSaveAsTemplate"
              v-model="currentItem.as_template"
              label="Save as Template"
            />
          </div>
        </div>

      </form>
    </validation-observer>
  </div>
</template>

<script>
import { fetchData } from '../../api/network'
import HeaderWithHelpButton from './HeaderWithHelpButton'

export default {
  props: {
    pageTitle: {
      type: String,
      default: ''
    },
    helpButtonUrl: {
      type: String,
      default: ''
    },
    infoTitle: {
      type: String,
      default: ''
    },
    infoDescription: {
      type: String,
      default: ''
    },
    isFetching: {
      type: Boolean,
      default: false
    },
    showIndicator: {
      type: Boolean,
      default: false
    },
    showSaveAsTemplate: {
      type: Boolean,
      default: false
    },
    templates: {
      type: Array,
      default: () => []
    },
    fields: {
      type: Array,
      default: () => []
    },
    item: {
      type: Object,
      default: () => {}
    },
    templateDataByIdUrl: {
      type: String,
      default: ''
    },
  },
  components: {
    HeaderWithHelpButton
  },
  data() {
    return {
      editMode: false,
      currentItem: {},
      templateId: null
    }
  },
  watch: {
    'templateId': {
      handler(newVal) {
        const baseUrl = `${this.templateDataByIdUrl}/${newVal}`

        if (newVal) {
          fetchData(baseUrl).then((resp) => {
            const id = this.currentItem.id ? this.currentItem.id : null

            const data = { ...resp.data.fields }
            delete data.id
            this.currentItem = data

            this.fields.forEach((field, index) => {
              if (field.type === 'editor') {
                $(`#editor-${index}`).summernote('code', resp.data.fields[`${field.name}`])
              }
            })

            if (id) {
              this.currentItem.id = id
            }
          })
            .catch(() => {})
        }
      },
      deep: false
    }
  },
  methods: {
    showEditMode() {
      this.currentItem = { ...this.item };

      this.fields.forEach((field, index) => {
        if (field.type === 'editor') {
          $(`#editor-${index}`).summernote('code', this.currentItem[`${field.name}`]);
        }
      })

      this.editMode = true
    },

    hideEditMode() {
      this.editMode = false
      this.templateId = null
      this.currentItem = {}
      this.currentItem.as_template = false
    },

    submit() {
      this.$refs.form.validate().then(success => {
        if (!success) {
          return
        }

        this.$emit('edit', this.currentItem, this.$refs.form, this.hideEditMode)

        this.$nextTick(() => {
          this.$refs.form.reset()
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.text-wrap {
  white-space: pre-wrap;
}
</style>
